import { mensagemMinimo3Caracteres } from '@core/util/mensagemUtil';
import { validateCnpj, validateCpf } from 'app/utils/validation';
import * as yup from 'yup';

export const isIbsCbsObrigatorio = (optanteSimples) => optanteSimples === 'FALSE';

const EmpresaSchema = yup
  .object()
  .shape({
    produtorRural: yup
      .string()
      .nullable()
      .required('Informe o produtor rural'),
    optanteSimples: yup
      .string()
      .nullable()
      .required('Informe o optante pelo SIMPLES'),
    ibsCbs: yup.boolean().when('optanteSimples', {
      is: 'FALSE',
      then: (schema) =>
        schema.oneOf(
          [true],
          'O preenchimento das informações de IBS/CBS - Reforma Tributária é obrigatório para empresas não optantes pelo SIMPLES'
        ),
    }),
    comercializacao: yup
      .string()
      .nullable()
      .required('Informe a comercialização'),
    regimeTributario: yup
      .string()
      .nullable()
      .required('Informe o regime tributário'),
    regimeTributarioEspecial: yup
      .string()
      .nullable()
      .required('Informe o regime tributário especial'),
    emailPrincipal: yup
      .string()
      .email('E-mail principal inválido')
      .nullable()
      .required('Informe o e-mail principal'),
    nomeRemetentePrincipal: yup
      .string()
      .nullable()
      .min(3, mensagemMinimo3Caracteres)
      .required('Informe o nome do remetente principal'),
    emailCobranca: yup
      .string()
      .email('E-mail de cobrança inválido')
      .nullable()
      .required('Informe o e-mail de cobrança'),
    nomeRemetenteCobranca: yup
      .string()
      .nullable()
      .min(3, mensagemMinimo3Caracteres)
      .required('Informe o nome do remetente de cobrança'),
    cstSituacaoTributaria: yup
      .object({
        id: yup.string().nullable(),
      })
      .nullable()
      .when('ibsCbs', {
        is: true,
        then: (schema) =>
          schema.test('cstSituacaoTributaria', 'Informe o código de situação tributária', (obj) => {
            if (!obj) return false;
            return !!obj.id;
          }),
      }),
    cstClassificacaoTributaria: yup
      .object()
      .nullable()
      .when('cstSituacaoTributaria.id', {
        is: (id) => Boolean(id),
        then: (schema) => schema.required('Informe o código de classificação tributária'),
      }),
    contribuinteIbs: yup
      .string()
      .nullable()
      .when('ibsCbs', {
        is: true,
        then: yup
          .string()
          .nullable()
          .required('Informe se é contribuinte IBS'),
      }),
    contribuinteCbs: yup
      .string()
      .nullable()
      .when('ibsCbs', {
        is: true,
        then: yup
          .string()
          .nullable()
          .required('Informe se é contribuinte CBS'),
      }),
    contribuinteIs: yup
      .string()
      .nullable()
      .when('ibsCbs', {
        is: true,
        then: yup
          .string()
          .nullable()
          .required('Informe se é contribuinte IS'),
      }),
    tipoIndicadorOperacao: yup
      .string()
      .nullable()
      .when('ibsCbs', {
        is: true,
        then: yup
          .string()
          .nullable()
          .test('tipoIndicadorOperacao', 'Informe o indicador de operação', (value) => !!value),
      }),
    codigoOperacaoIbsCbs: yup
      .object({
        id: yup.string().nullable(),
        codigo: yup.string().nullable(),
      })
      .nullable()
      .when('ibsCbs', {
        is: true,
        then: (schema) =>
          schema.test('codigoOperacaoIbsCbs', 'Informe o código de operação IBS/CBS', (obj) => {
            if (!obj) return false;
            return !!(obj.id || obj.codigo);
          }),
      }),

    pessoa: yup.object().shape({
      nome: yup
        .string()
        .min(3, mensagemMinimo3Caracteres)
        .nullable()
        .required('Informe o nome'),
      tipoPessoa: yup.string().nullable(),
      inscricaoMunicipal: yup.string().test('teste-inscricaoMunicipal', mensagemMinimo3Caracteres, (value) => {
        return !(value && value.length < 3);
      }),
      pessoaJuridica: yup.object().shape({
        cnpj: yup.string().test('test cnpj', 'CNPJ inválido', (value) => {
          if (value) {
            return validateCnpj(value);
          }
          return true;
        }),
        razaoSocial: yup.string().test('teste-razaoSocial', mensagemMinimo3Caracteres, (value) => {
          return !(value && value.length < 3);
        }),
        inscricaoEstadual: yup.string().test('test-inscricaoEstadual', mensagemMinimo3Caracteres, (value) => {
          return !(value && value.length < 3);
        }),
      }),

      pessoaFisica: yup.object().shape({
        cpf: yup.string().test('test cpf', 'CPF inválido', (value) => {
          if (value) {
            const validate = validateCpf(value);
            return validate;
          }
          return true;
        }),
        identidade: yup.string().min(3, mensagemMinimo3Caracteres),
        dataNascimento: yup.object().nullable(),
        identificadorEstrangeiro: yup.string().min(3, mensagemMinimo3Caracteres),
      }),

      contatos: yup
        .array()
        .of(
          yup.object().shape({
            nomeReferencia: yup.string().test('teste-nomeReferencia', mensagemMinimo3Caracteres, (value) => {
              return !(value && value.length < 3);
            }),
            email: yup.string().email('E-mail inválido'),
            emailsAdicionais: yup.array().of(
              yup.object().shape({
                email: yup.string().email('E-mail inválido'),
              })
            ),
          })
        )
        .nullable(),

      endereco: yup.object().shape({
        bairro: yup.string().test('teste-bairro', mensagemMinimo3Caracteres, (value) => {
          return !(value && value.length < 3);
        }),
      }),
    }),
  })
  .nullable();

export default EmpresaSchema;
